import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthContext } from "@/features/auth/hooks/use-auth-context";
import { apiFetch } from "@/shared/lib/bff/client";
import { logger } from "@/shared/lib/logger";

export const followKeys = {
  status: (targetUserId: string) => ["follow-status", targetUserId] as const,
  list: (targetUserId: string, type: "followers" | "following") =>
    ["follow-list", targetUserId, type] as const,
};

export interface FollowProfile {
  userId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
}

export function useFollowersList(
  targetUserId: string,
  type: "followers" | "following"
) {
  return useQuery({
    queryKey: followKeys.list(targetUserId, type),
    queryFn: () =>
      apiFetch<FollowProfile[]>(
        `/api/followers/${targetUserId}/list?type=${type}`
      ),
    enabled: Boolean(targetUserId),
    staleTime: 30_000,
  });
}

export interface FollowStatus {
  isFollowing: boolean;
  followerCount: number;
  followingCount: number;
}

export function useFollowStatus(targetUserId: string) {
  return useQuery({
    queryKey: followKeys.status(targetUserId),
    queryFn: () => apiFetch<FollowStatus>(`/api/followers/${targetUserId}`),
    enabled: Boolean(targetUserId),
    staleTime: 30_000,
  });
}

export function useToggleFollow(targetUserId: string) {
  const { session } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (currentlyFollowing: boolean) => {
      const action = currentlyFollowing ? "unfollow" : "follow";
      await apiFetch(`/api/followers/${targetUserId}`, {
        method: currentlyFollowing ? "DELETE" : "POST",
      });

      logger.info("Follow toggled", { targetUserId, action });
    },
    onMutate: async (currentlyFollowing) => {
      const myUserId = session?.user.id;
      await queryClient.cancelQueries({
        queryKey: followKeys.status(targetUserId),
      });
      const previous = queryClient.getQueryData<FollowStatus>(
        followKeys.status(targetUserId)
      );
      const previousMine = myUserId
        ? queryClient.getQueryData<FollowStatus>(followKeys.status(myUserId))
        : undefined;
      queryClient.setQueryData(followKeys.status(targetUserId), {
        ...previous,
        isFollowing: !currentlyFollowing,
        followerCount:
          (previous?.followerCount ?? 0) + (currentlyFollowing ? -1 : 1),
      });
      if (myUserId && previousMine) {
        queryClient.setQueryData(followKeys.status(myUserId), {
          ...previousMine,
          followingCount:
            (previousMine.followingCount ?? 0) + (currentlyFollowing ? -1 : 1),
        });
      }
      return { previous, previousMine, myUserId };
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData(
        followKeys.status(targetUserId),
        context?.previous
      );
      if (context?.myUserId && context?.previousMine) {
        queryClient.setQueryData(
          followKeys.status(context.myUserId),
          context.previousMine
        );
      }
    },
    onSettled: (_data, _err, _vars, context) => {
      queryClient.invalidateQueries({
        queryKey: followKeys.status(targetUserId),
      });
      if (context?.myUserId) {
        queryClient.invalidateQueries({
          queryKey: followKeys.status(context.myUserId),
        });
      }
    },
  });
}
